import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// FR (langue par défaut / fallback) reste embarqué dans le chunk d'entrée :
// il doit être disponible au premier render, sans requête.
import fr from './locales/fr/common.json';
import frPro from './locales/fr/pro.json';

// AR / EN sont chargés à la demande (import dynamique → chunk séparé par
// locale). Embarquer les 3 langues coûtait plusieurs centaines de Ko dans
// l'entrée pour des utilisateurs qui n'en consultent qu'une.
// NB : ce module est web-only (erp-web / erp-pro / storefront) — le mobile a
// son propre bootstrap (apps/*/src/lib/i18n.ts) qui importe les JSON en dur.
const LAZY_LOCALES: Record<string, () => Promise<Record<string, unknown>>> = {
  ar: async () => {
    const [common, pro] = await Promise.all([
      import('./locales/ar/common.json'),
      import('./locales/ar/pro.json'),
    ]);
    return { ...common.default, ...pro.default };
  },
  en: async () => {
    const [common, pro] = await Promise.all([
      import('./locales/en/common.json'),
      import('./locales/en/pro.json'),
    ]);
    return { ...common.default, ...pro.default };
  },
};

const resources = {
  fr: { translation: { ...fr, ...frPro } },
};

/** Charge (une fois) le bundle de la locale demandée dans i18next. */
export async function ensureLocaleLoaded(
  lng: string | undefined,
): Promise<boolean> {
  const base = (lng ?? '').split('-')[0] ?? '';
  const loader = LAZY_LOCALES[base];
  if (!loader || i18n.hasResourceBundle(base, 'translation')) return true;
  try {
    const data = await loader();
    i18n.addResourceBundle(base, 'translation', data, true, false);
    return true;
  } catch {
    // Réseau/chunk indisponible → on reste sur le fallback FR plutôt que de
    // bloquer le boot. Le prochain changeLanguage retentera.
    return false;
  }
}

const initialization = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fr',
    supportedLngs: ['fr', 'ar', 'en'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

// Les changements de langue attendent le bundle AVANT de basculer — pas de
// flash de clés brutes ni de mix de langues pendant le fetch.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lng?: string, ...rest: unknown[]) => {
  await ensureLocaleLoaded(lng);
  return (originalChangeLanguage as (...args: unknown[]) => Promise<unknown>)(
    lng,
    ...rest,
  );
}) as typeof i18n.changeLanguage;

/**
 * Met à jour l'attribut dir/lang sur le <html>. Arabe = RTL, autres = LTR.
 */
function applyDocumentDirection(lng: string) {
  if (typeof document === 'undefined') return;
  const dir = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
}

// On subsequent language switches.
i18n.on('languageChanged', applyDocumentDirection);

// Detection is asynchronous. Reading i18n.language immediately after init()
// races localStorage detection and can see `undefined`, which used to skip the
// AR/EN lazy bundle forever while still flipping the document direction later.
// Wait for initialization, then load the detected bundle and re-signal the
// language so react-i18next replaces the temporary French fallback.
void initialization.then(async () => {
  const detectedLng = i18n.language ?? 'fr';
  applyDocumentDirection(detectedLng);
  if (!LAZY_LOCALES[detectedLng.split('-')[0] ?? '']) return;
  await ensureLocaleLoaded(detectedLng);
  await originalChangeLanguage(detectedLng);
});

export default i18n;

export { readErrorDiagnostics, salesMutationErrorTranslationKey } from './sales-mutation-errors';

export { stockVehicleMutationErrorKey } from './stock-vehicle-errors';
